import { createContext, useContext, useState, useRef, useCallback, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MwlSymbol } from "@/components/MwlSymbol";
import { useAuth } from "@/context/AuthContext";
import { ProfileDialog } from "@/components/ProfileDialog";

export const ViewerContext = createContext(null);
export const useProfileViewer = () => useContext(ViewerContext);

const NavGuardContext = createContext(null);
export const useNavGuard = () => useContext(NavGuardContext);

const mainTabs = [
  { to: "/challenge", label: "CHALLENGE" },
  { to: "/ask", label: "ASK" },
  { to: "/answer", label: "ANSWER" },
];

function TopLink({ to, label, testid, onClick }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      onClick={onClick}
      className="text-sm tracking-widest transition-colors duration-200"
      style={({ isActive }) => ({
        color: isActive ? "var(--accent)" : "var(--text-muted)",
        paddingBottom: "4px",
        borderBottom: isActive ? "1px solid var(--accent)" : "1px solid transparent",
      })}
    >
      {label}
    </NavLink>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [viewUserId, setViewUserId] = useState(null);
  const [viewOpts, setViewOpts] = useState({});

  const guard = useRef(null);
  const registerGuard = useCallback((g) => { guard.current = g; }, [guard]);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  const viewProfile = useCallback((id, opts = {}) => { setViewUserId(id); setViewOpts(opts); }, []);
  const viewerValue = useMemo(() => ({ viewProfile }), [viewProfile]);
  const guardValue = useMemo(() => ({ registerGuard }), [registerGuard]);

  const runGuarded = (proceed) => {
    if (guard.current?.isDirty?.()) setPending({ proceed });
    else proceed();
  };

  const linkClick = (to) => (e) => {
    if (guard.current?.isDirty?.()) {
      e.preventDefault();
      setPending({ proceed: () => navigate(to) });
    }
  };

  const confirmSaveLeave = async () => {
    setSaving(true);
    try { await guard.current?.save?.(); } finally { setSaving(false); }
    const p = pending;
    guard.current = null;
    setPending(null);
    p?.proceed?.();
  };

  const confirmLeave = () => {
    const p = pending;
    guard.current = null;
    setPending(null);
    p?.proceed?.();
  };

  return (
    <ViewerContext.Provider value={viewerValue}>
      <NavGuardContext.Provider value={guardValue}>
        <div className="min-h-screen flex flex-col">
          <header
            className="w-full"
            style={{ borderBottom: "1px solid var(--border)" }}
          >
            <div className="max-w-5xl mx-auto px-6 py-5 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
              <nav className="flex items-center gap-4 sm:gap-8 flex-wrap">
                {mainTabs.map((t) => (
                  <TopLink key={t.to} to={t.to} label={t.label} testid={`nav-${t.label.toLowerCase()}`} onClick={linkClick(t.to)} />
                ))}
              </nav>
              <NavLink to="/home" onClick={linkClick("/home")} data-testid="nav-home-symbol" className="flex justify-center transition-opacity duration-200 hover:opacity-80">
                <MwlSymbol size={44} strokeWidth={5} testId="nav-symbol" />
              </NavLink>
              <div className="flex items-center justify-end gap-4 sm:gap-8 flex-wrap">
                <TopLink to="/chat" label="CHAT" testid="nav-dive" onClick={linkClick("/chat")} />
                <TopLink to="/shop" label="SHOP" testid="nav-shop" onClick={linkClick("/shop")} />
                <NavLink
                  to="/profile"
                  data-testid="nav-profile"
                  onClick={linkClick("/profile")}
                  className="text-sm tracking-widest transition-colors duration-200"
                  style={({ isActive }) => ({
                    color: isActive ? "var(--accent)" : "var(--text-muted)",
                    paddingBottom: "4px",
                    borderBottom: isActive ? "1px solid var(--accent)" : "1px solid transparent",
                  })}
                >
                  PROFILE
                </NavLink>
                <button
                  onClick={() => runGuarded(() => { logout(); navigate("/login"); })}
                  data-testid="logout-btn"
                  className="text-sm tracking-widest transition-colors duration-200"
                  style={{ color: "var(--text-muted)", paddingBottom: "4px", borderBottom: "1px solid transparent" }}
                >
                  EXIT
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 w-full">
            <div className="max-w-5xl mx-auto px-6 py-12 fade-in">{children}</div>
          </main>
        </div>

        {pending && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-6 fade-in"
            style={{ background: "rgba(0,0,0,0.75)" }}
            data-testid="unsaved-overlay"
          >
            <div
              className="w-full max-w-md p-8"
              style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
              data-testid="unsaved-modal"
            >
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Unsaved changes</span>
              <p className="text-base leading-relaxed mt-3 mb-8" style={{ color: "var(--text-primary)" }}>
                you have unsaved changes. do you want to save before leaving?
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={confirmSaveLeave}
                  disabled={saving}
                  data-testid="unsaved-save-btn"
                  className="btn w-full py-3 text-sm tracking-widest"
                >
                  {saving ? "…" : "SAVE & LEAVE"}
                </button>
                <button
                  onClick={confirmLeave}
                  data-testid="unsaved-discard-btn"
                  className="btn w-full py-3 text-sm tracking-widest"
                >
                  LEAVE WITHOUT SAVING
                </button>
                <button
                  onClick={() => setPending(null)}
                  data-testid="unsaved-cancel-btn"
                  className="text-xs tracking-widest mt-1 opacity-60 hover:opacity-100"
                  style={{ color: "var(--text-secondary)" }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}

        <ProfileDialog userId={viewUserId} noReview={viewOpts.noReview} onClose={() => setViewUserId(null)} />
      </NavGuardContext.Provider>
    </ViewerContext.Provider>
  );
}
